import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaFileInvoice, FaTag } from "react-icons/fa";
import { getPurchaseRequests, getPurchaseRequestInbox } from "redux/erp/purchaseRequestSlice";
import { EmptyState, LoadingSpinner } from "components/common";
import PageShell, { PageTitle } from "../purchase/common/PageShell";
import FilterDropdown from "../purchase/common/FilterDropdown";
import Clickable from "../purchase/common/Clickable";
import PurchaseRequestDrawer from "./PurchaseRequestDrawer";
import PurchaseSignerSettingsModal from "../purchase/settings/PurchaseSignerSettingsModal";

const initialsOf = (s) => (String(s || "?").trim().slice(0, 2) || "?").toUpperCase();

const FORM_TYPES = [
  { value: "raw_material", label: "Raw Material", iso: "QF-LGS-01", desc: "Raw material / finished goods", icon: "bi-box-seam" },
  { value: "non_raw_material_service", label: "Non-Raw / Services", iso: "QF-PUR-01", desc: "Assets, non-raw items, services", icon: "bi-tools" },
];

const STATUS = {
  0: { name: "Draft", dot: "tw-bg-geist-fg-3", cls: "tw-bg-geist-100 tw-text-geist-fg-2 tw-border-geist-border" },
  1: { name: "Pending", dot: "tw-bg-geist-warning", cls: "tw-bg-[#fff8ee] tw-text-[#b46a00] tw-border-[#f7e2c0]" },
  2: { name: "Approved", dot: "tw-bg-geist-success", cls: "tw-bg-[#e9f9f6] tw-text-[#04766a] tw-border-[#bfeae4]" },
  3: { name: "Rejected", dot: "tw-bg-geist-error", cls: "tw-bg-[#fdeceb] tw-text-[#c0362f] tw-border-[#f6cfcc]" },
  4: { name: "Canceled", dot: "tw-bg-geist-fg-3", cls: "tw-bg-geist-100 tw-text-geist-fg-3 tw-border-geist-border" },
};
const STATUS_OPTIONS = [0, 1, 2, 3, 4].map((id) => ({ value: id, label: STATUS[id].name }));

const TH = "tw-px-4 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-whitespace-nowrap tw-bg-geist-bg-2";

const PurchaseRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { allData, inboxData, loading } = useSelector((s) => s.erp_purchase_request);
  const isAdmin = useSelector((s) => s.profile_user?.data?.is_admin);

  const [mode, setMode] = useState("mine");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState("raw_material");
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const openNew = (type) => { setDrawerType(type); setNewMenuOpen(false); setDrawerOpen(true); };

  // "mine" mode gọi scope=all — Purchase Requisition ai cũng xem được (chỉ sửa/hủy/ký mới
  // giới hạn theo chủ phiếu/người ký), nên danh sách này thực chất là TẤT CẢ requisitions,
  // không riêng của người đang đăng nhập.
  const fetchList = () => {
    if (mode === "inbox") dispatch(getPurchaseRequestInbox());
    else dispatch(getPurchaseRequests({ scope: "all" }));
  };
  useEffect(fetchList, [dispatch, mode]);

  const rawRows = useMemo(
    () => ((mode === "inbox" ? inboxData?.rows : allData?.rows) || []).map((r) => r.attributes || r),
    [mode, inboxData, allData]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rawRows.filter((a) => {
      const sid = a.status?.id ?? a.status;
      if (mode === "mine" && statusFilter.length && !statusFilter.includes(sid)) return false;
      if (!q) return true;
      return [a.code, a.department, a.requestor, a.site_name, a.current_approver_email]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rawRows, search, statusFilter, mode]);

  const header = (
    <div className="tw-px-4 sm:tw-px-6 tw-pt-4 tw-pb-3">
      <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3 tw-mb-3">
        <PageTitle subtitle="Purchase Requisition — QF-LGS-01 / QF-PUR-01">Purchase Requisitions</PageTitle>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2 tw-h-9 tw-w-64 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border-2 focus-within:tw-border-geist-accent tw-transition-colors">
            <i className="bi bi-search tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code, department, site..."
              className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
            />
            {search && (
              <Clickable onClick={() => setSearch("")} className="tw-text-geist-fg-3 hover:tw-text-geist-fg">
                <i className="bi bi-x-lg tw-text-[11px]" />
              </Clickable>
            )}
          </div>

          <Clickable onClick={() => setSettingsOpen(true)}
            title={isAdmin ? "Settings (signature + signer settings)" : "My signature settings"}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100 hover:tw-text-geist-fg tw-transition-colors">
            <i className="bi bi-gear tw-text-sm" /> Settings
          </Clickable>

          <div className="tw-relative">
            <Clickable onClick={() => setNewMenuOpen((o) => !o)}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2 tw-transition-colors">
              <i className="bi bi-plus-lg tw-text-sm" /> New requisition
              <i className={`bi ${newMenuOpen ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[10px] tw-opacity-80`} />
            </Clickable>
            {newMenuOpen && (
              <>
                <div className="tw-fixed tw-inset-0 tw-z-40" onClick={() => setNewMenuOpen(false)} />
                <div className="tw-absolute tw-right-0 tw-mt-1 tw-w-72 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-z-50 tw-overflow-hidden tw-py-1">
                  {FORM_TYPES.map((ft) => (
                    <Clickable key={ft.value} onClick={() => openNew(ft.value)}
                      className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 hover:tw-bg-geist-100 tw-transition-colors">
                      <i className={`bi ${ft.icon} tw-text-base tw-text-geist-fg-2 tw-w-4 tw-text-center tw-flex-shrink-0`} />
                      <span className="tw-flex-1 tw-min-w-0">
                        <span className="tw-block tw-text-[12px] tw-font-semibold tw-text-geist-fg tw-truncate">{ft.label}</span>
                        <span className="tw-block tw-text-[10px] tw-text-geist-fg-3 tw-truncate">{ft.desc}</span>
                      </span>
                      <span className="tw-text-[9px] tw-font-semibold tw-font-geist-mono tw-text-geist-fg-3 tw-flex-shrink-0">{ft.iso}</span>
                    </Clickable>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <div className="tw-inline-flex tw-p-0.5 tw-bg-geist-100 tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm">
          {[
            { k: "mine", label: "All requisitions", icon: "bi-person-lines-fill" },
            { k: "inbox", label: "Waiting for my signature", icon: "bi-pen" },
          ].map((m) => (
            <Clickable key={m.k} onClick={() => setMode(m.k)}
              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-[4px] tw-transition ${
                mode === m.k ? "tw-bg-geist-bg tw-text-geist-fg tw-shadow-geist-card" : "tw-text-geist-fg-3 hover:tw-text-geist-fg"
              }`}>
              <i className={`bi ${m.icon} tw-text-[11px]`} /> {m.label}
            </Clickable>
          ))}
        </div>

        {mode === "mine" && (
          <FilterDropdown label="Status" icon={FaTag} value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
        )}

        <span className="tw-text-xs tw-text-geist-fg-3 tw-ml-auto">{rows.length} item(s)</span>
      </div>
    </div>
  );

  return (
    <>
      <PageShell header={header}>
        {loading ? (
          <LoadingSpinner message="Loading..." />
        ) : rows.length === 0 ? (
          <EmptyState icon={FaFileInvoice}
            title={search || statusFilter.length ? "No matching requisitions" : "No purchase requisitions"}
            description={search || statusFilter.length ? "Try changing the search or status filter." : ""} />
        ) : (
          <div className="tw-p-4 sm:tw-p-6">
            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
              <table className="tw-w-full tw-border-collapse">
                <thead className="tw-sticky tw-top-0 tw-z-10">
                  <tr className="tw-border-b tw-border-solid tw-border-geist-border">
                    <th className={TH}>Requisition</th>
                    <th className={TH}>Site</th>
                    <th className={TH}>Department</th>
                    <th className={TH}>Prepared by</th>
                    {mode === "inbox" && <th className={TH}>Pending signer</th>}
                    <th className={TH}>Status</th>
                    <th className={`${TH} tw-w-8`} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const st = STATUS[a.status?.id ?? a.status] || STATUS[0];
                    const ft = FORM_TYPES.find((f) => f.value === a.form_type);
                    return (
                      <tr key={a.id} onClick={() => navigate(`/purchase-requests/${a.id}`)}
                        className="tw-group tw-border-b tw-border-solid tw-border-geist-border last:tw-border-0 hover:tw-bg-geist-bg-2 tw-cursor-pointer tw-transition-colors">
                        <td className="tw-px-4 tw-py-3 tw-align-middle">
                          <div className="tw-flex tw-items-center tw-gap-2.5">
                            <div className="tw-shrink-0 tw-w-9 tw-h-9 tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-semibold tw-uppercase">
                              {initialsOf(a.code)}
                            </div>
                            <div className="tw-min-w-0 tw-flex-1">
                              <div className="tw-flex tw-items-center tw-gap-1.5">
                                <span className="tw-text-[13px] tw-font-semibold tw-text-geist-fg tw-truncate">{a.code}</span>
                                {ft && (
                                  <span className="tw-shrink-0 tw-text-[9px] tw-font-medium tw-px-1.5 tw-py-px tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-border tw-border-solid tw-border-geist-border">
                                    {ft.label}
                                  </span>
                                )}
                              </div>
                              <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate">{a.vendor_name || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-[13px] tw-text-geist-fg-2">{a.site_name || "—"}</td>
                        <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-[13px] tw-text-geist-fg-2">{a.department || "—"}</td>
                        <td className="tw-px-4 tw-py-3 tw-align-middle">
                          <div className="tw-text-[13px] tw-text-geist-fg tw-truncate">{a.requestor || "—"}</div>
                          {a.created_date && <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate">{a.created_date}</div>}
                        </td>
                        {mode === "inbox" && (
                          <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-[12px] tw-text-geist-fg-2 tw-truncate">
                            {a.current_approver_email || "—"}
                          </td>
                        )}
                        <td className="tw-px-4 tw-py-3 tw-align-middle">
                          <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-xl tw-border tw-border-solid ${st.cls}`}>
                            <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-xl ${st.dot}`} />
                            {st.name}
                          </span>
                        </td>
                        <td className="tw-px-2 tw-py-3 tw-align-middle tw-text-right">
                          <i className="bi bi-chevron-right tw-text-[11px] tw-text-geist-fg-3 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </PageShell>

      <PurchaseRequestDrawer
        show={drawerOpen}
        onHide={() => { setDrawerOpen(false); setDrawerType("raw_material"); }}
        purchaseRequest={null}
        initialFormType={drawerType}
        onSaved={(newId) => { if (newId) { navigate(`/purchase-requests/${newId}`); } else { setMode("mine"); fetchList(); } }}
      />

      <PurchaseSignerSettingsModal show={settingsOpen} onHide={() => setSettingsOpen(false)} isAdmin={isAdmin} />
    </>
  );
};

export default PurchaseRequests;
